import { useEffect } from 'react';

import { DEFAULT_ROLE_OPTIONS_LIMIT } from 'bundles/onboarding-common/constants';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import { useOnboardingQuestionData } from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useBaseSelectState from 'bundles/onboarding-common/hooks/useBaseSelectState';
import useHandleSelectedItemsChange from 'bundles/onboarding-common/hooks/useHandleSelectedItemsChange';
import useIsAdvancer from 'bundles/onboarding-common/hooks/useIsAdvancer';
import type { RoleFragment } from 'bundles/onboarding-common/queries/__generated__/RoleFragment';
import type { Role, RoleWithState, SelectOptionWithState } from 'bundles/onboarding-common/types';
import { getIdontKnowOption } from 'bundles/onboarding-common/utils';

import _t from 'i18n!nls/onboarding-common';

const getInitialState = (defaultValue: RoleWithState[], roles: RoleFragment[]): RoleWithState[] => {
  const allRolesWithState = roles.reduce((acc, role) => {
    const ids = acc.map(({ id }) => id);
    if (!ids.includes(role.id)) {
      const roleWithState: RoleWithState = {
        ...role,
        type: 'DEFAULT',
        isSearchResult: false,
        isSelected: false,
        isVisible: true,
        isPartOfSearch: false,
      };
      return [...acc, roleWithState];
    }
    return acc;
  }, defaultValue);

  return allRolesWithState;
};

const useRoleSelectQuestionState = ({
  defaultValue,
  roles,
  defaultSingleSelectOptionIsSelected = false,
}: {
  defaultValue: RoleWithState[];
  roles: RoleFragment[];
  defaultSingleSelectOptionIsSelected?: boolean;
}) => {
  const isAdvancer = useIsAdvancer();
  const { onChange, setValidationErrorMessage } = useOnboardingQuestionData();

  const defaultSingleSelectOption: SelectOptionWithState<Role> = {
    ...getIdontKnowOption(),
    slug: 'i-dont-know',
    isSelected: defaultSingleSelectOptionIsSelected,
  };

  const {
    itemsList,
    itemsPerRow: rolesPerRow,
    containerRef,
    handleClick,
    onViewMoreClick,
    onViewAllClick,
    isLoading,
    isExpanded,
    iDontKnowOption: iDontKnowRole,
    shouldUseDefaultLayout,
    hasShownAllItems,
  } = useBaseSelectState({
    questionId: QUESTION_IDS.roleSelect,
    defaultSingleSelectOption,
    getDisplayName: (item) => item.name || '',
    widthData: {
      itemPadding: 16,
      iconWidth: 16,
      spacingBetweenIconAndText: 4,
    },
    initialState: getInitialState(defaultValue, roles),
  });

  const handleSelectedRolesChange = (allRoles: SelectOptionWithState<Role>[]) => {
    const selectedRoles = allRoles
      .filter(({ isSelected }) => isSelected)
      .map(({ id, name, type }) => ({ id, name: name || '', type }));

    onChange({
      __typename: QUESTION_IDS.roleSelect,
      value: selectedRoles,
    });
  };

  useHandleSelectedItemsChange([...itemsList, ...(iDontKnowRole ? [iDontKnowRole] : [])], handleSelectedRolesChange);

  const rolesLimit = isAdvancer ? 1 : DEFAULT_ROLE_OPTIONS_LIMIT;

  useEffect(() => {
    if (itemsList.filter(({ isSelected }) => isSelected).length > rolesLimit) {
      setValidationErrorMessage(_t("You've reached the limit of #{rolesLimit} selected roles", { rolesLimit }));
    } else {
      setValidationErrorMessage(undefined);
    }
  }, [itemsList, rolesLimit, setValidationErrorMessage]);

  return {
    rolesPerRow,
    containerRef,
    handleClick,
    onViewMoreClick,
    onViewAllClick,
    isLoading,
    isExpanded,
    iDontKnowRole,
    shouldUseDefaultLayout,
    hasShownAllItems,
  };
};

export default useRoleSelectQuestionState;
